// ✅ src/pages/VehicleV2/BatteryCardEntry.jsx  (FULL FILE)
// Backend mount:
//   app.use("/api/batteryCard", batteryCard);
//
// Routes used:
//   GET  https://nissi-india.com/api/batteryCard/dropdowns?branchId=..
//   GET  https://nissi-india.com/api/batteryCard/code?loginUserId=..
//   GET  https://nissi-india.com/api/batteryCard/view?batteryId=..
//   GET  https://nissi-india.com/api/batteryCard/vehicle-regno?vehicleId=..
//   GET  https://nissi-india.com/api/batteryCard/serial-details?serialNo=..&branchId=..
//   POST https://nissi-india.com/api/batteryCard/save
//
// ✅ Notes:
// - Supports add / edit / optional view mode
// - Sends fin-year headers if available
// - Dropdowns come from backend
// - Edit loads by querystring ?batteryId=..
// - View mode can be opened with ?view=1&batteryId=..
// - Save updates if batteryId > 0 else inserts

import React, { useEffect, useMemo, useState, useContext, useCallback } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import VehicleLayoutWrapper from "../../components/VehicleLayoutWrapper";
import getCurrentSkin from "../../utils/getCurrentSkin";
import { ThemeContext } from "../../context/ThemeContext";
import { toast, ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FaSave, FaTimes, FaPlus } from "react-icons/fa";

const API_BASE = "https://nissi-india.com/api/batteryCard";

const toInt = (v, def = 0) => {
  const n = Number(v);
  return Number.isFinite(n) ? Math.trunc(n) : def;
};

// "dd/MM/yyyy" -> "yyyy-mm-dd"
const dmyToIso = (dmy) => {
  if (!dmy) return "";
  const s = String(dmy).trim();
  const m = /^(\d{2})\/(\d{2})\/(\d{4})$/.exec(s);
  if (!m) return "";
  return `${m[3]}-${m[2]}-${m[1]}`;
};

const BatteryCardEntry = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const { isDark, skin, setSkin } = useContext(ThemeContext);
  const currentSkin = getCurrentSkin(skin);
  const { baseColor, baseShade } = currentSkin;

  const buttonClass = `bg-${baseColor}-${baseShade}`;
  const hoverClass = `hover:bg-${baseColor}-700`;

  const batteryIdFromUrl = useMemo(() => toInt(searchParams.get("batteryId")), [searchParams]);
  const isEdit = batteryIdFromUrl > 0;
  const isView = searchParams.get("view") === "1";

  const loginUserId =
    localStorage.getItem("userId") ||
    localStorage.getItem("loginuserid") ||
    "";

  const branchId = Number(localStorage.getItem("branchid") || 8);

  // Optional financial year headers for new backend resolver
  const finYear =
    localStorage.getItem("finyear") ||
    localStorage.getItem("financialyear") ||
    sessionStorage.getItem("finyear") ||
    "";

  const finYearId =
    localStorage.getItem("finyearid") ||
    localStorage.getItem("financialyearid") ||
    sessionStorage.getItem("finyearid") ||
    "";

  const requestConfig = useMemo(() => {
    const headers = {};

    if (finYear) headers["x-finyear"] = finYear;
    if (finYearId) {
      headers["x-finyearid"] = finYearId;
      headers["x-finyear-id"] = finYearId;
    }

    return { headers };
  }, [finYear, finYearId]);

  // Dropdowns
  const [suppliers, setSuppliers] = useState([]);
  const [stores, setStores] = useState([]);
  const [vehicleCodes, setVehicleCodes] = useState([]);
  const [vehicleRegNos, setVehicleRegNos] = useState([]);
  const [expectedLifeYears, setExpectedLifeYears] = useState(["Select"]);
  const [expectedLifeMonths, setExpectedLifeMonths] = useState(["Select"]);
  const [showSrcSerialNo, setShowSrcSerialNo] = useState(true);

  const [loading, setLoading] = useState(false);

  const [formData, setFormData] = useState({
    batteryId: 0,
    batteryCode: "",
    dateOfReceipt: "",

    vehicleId: "",
    vehicleRegNoId: "",

    batterySerialNo: "",
    batteryType: "",
    batteryMake: "",

    expectedLifeYear: "Select",
    expectedLifeMonth: "Select",

    purchasedFromId: "0",
    purchaseAmt: "",
    purchasedDate: "",
    warrantyFrom: "",
    warrantyTo: "",

    remarks: "",
    storeId: "",

    srcSerialNo: "",
  });

  const labelClass = `block text-sm font-medium mb-2 ${
    isDark ? "text-gray-300" : "text-gray-700"
  }`;

  const inputClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white placeholder-gray-400"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const selectClass = `w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent ${
    isDark
      ? "bg-gray-700/30 border-gray-600 text-white"
      : "bg-white border-gray-300 text-gray-900"
  }`;

  const readOnlyFieldClass = isView ? "opacity-80 cursor-not-allowed" : "";

  const setField = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  /* ------------------------- LOAD DROPDOWNS ------------------------- */
  const loadDropdowns = useCallback(async () => {
    try {
      if (!branchId) return;

      const { data } = await axios.get(
        `${API_BASE}/dropdowns`,
        {
          params: { branchId },
          ...requestConfig,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Failed to load dropdowns");
      }

      setSuppliers(data.suppliers || []);
      setStores(data.stores || []);
      setVehicleCodes(data.vehicleCodes || []);
      setVehicleRegNos(data.vehicleRegNos || []);
      setExpectedLifeYears(data.expectedLifeYears || ["Select"]);
      setExpectedLifeMonths(data.expectedLifeMonths || ["Select"]);
      setShowSrcSerialNo(!!data.showSrcSerialNo);
    } catch (err) {
      throw new Error(err?.response?.data?.message || err?.message || "Dropdowns error");
    }
  }, [branchId, requestConfig]);

  /* ------------------------- CODE GENERATION ------------------------- */
  const loadNewCode = useCallback(async () => {
    try {
      const { data } = await axios.get(
        `${API_BASE}/code`,
        {
          params: { loginUserId },
          ...requestConfig,
        }
      );

      if (!data?.success) {
        throw new Error(data?.message || "Failed to generate code");
      }

      setField("batteryCode", data.batteryCode || "");
    } catch (err) {
      throw new Error(err?.response?.data?.message || err?.message || "Code generation failed");
    }
  }, [loginUserId, requestConfig]);

  /* ------------------------- VEHICLE REG NO ------------------------- */
  const loadVehicleRegNos = useCallback(
    async (vehicleId) => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/vehicle-regno`,
          {
            params: { vehicleId },
            ...requestConfig,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load vehicle reg no");
        }

        setVehicleRegNos(data.vehicleRegNos || []);
      } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || "Vehicle reg no load failed");
      }
    },
    [requestConfig]
  );

  /* ------------------------- EDIT LOAD ------------------------- */
  const loadForEdit = useCallback(
    async (batteryId) => {
      try {
        const { data } = await axios.get(
          `${API_BASE}/view`,
          {
            params: { batteryId },
            ...requestConfig,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load battery");
        }

        const d = data.data || {};

        setFormData((prev) => ({
          ...prev,
          batteryId: toInt(d.fld_batteryid),
          batteryCode: d.fld_batterycode || "",
          dateOfReceipt: dmyToIso(d.dateOfReceipt || d.fld_dateofreceipt),

          vehicleId: String(d.fld_vehicleid ?? ""),
          vehicleRegNoId: String(d.fld_vehicleid ?? ""),

          batterySerialNo: d.fld_battery_serialno || "",
          batteryType: d.fld_batterytype || "",
          batteryMake: d.fld_batterymake || "",

          expectedLifeYear: d.fld_expectedlifeyear || "Select",
          expectedLifeMonth: d.fld_expectedlifemonth || "Select",

          purchasedFromId: String(d.fld_purchasedfrom ?? "0"),
          purchaseAmt: d.fld_purchaseamt ?? "",
          purchasedDate: dmyToIso(d.purchasedDate || d.fld_purchaseddate),

          warrantyFrom: dmyToIso(d.warrentyFrom),
          warrantyTo: dmyToIso(d.warrentyTo),

          remarks: d.fld_remarks || "",
          storeId: String(d.fld_storeid ?? ""),

          srcSerialNo: d.fld_srcserialno || "",
        }));

        if (d.fld_vehicleid) {
          await loadVehicleRegNos(d.fld_vehicleid);
        }
      } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || "Edit load failed");
      }
    },
    [loadVehicleRegNos, requestConfig]
  );

  /* ------------------------- SERIAL DETAILS ------------------------- */
  const loadSerialDetails = useCallback(
    async (serialNo) => {
      try {
        if (!serialNo) return;

        const { data } = await axios.get(
          `${API_BASE}/serial-details`,
          {
            params: { serialNo, branchId },
            ...requestConfig,
          }
        );

        if (!data?.success) {
          throw new Error(data?.message || "Failed to load serial details");
        }

        const d = data.data;
        if (!d) return;

        setFormData((prev) => ({
          ...prev,
          batteryMake: d.batteryMake || prev.batteryMake,
          purchaseAmt: d.purchaseAmt || prev.purchaseAmt,
          purchasedDate: dmyToIso(d.issuedDate) || prev.purchasedDate,
          srcSerialNo: d.srcSerialNo || prev.srcSerialNo,
          purchasedFromId: d.supplier?.id ? String(d.supplier.id) : prev.purchasedFromId,
        }));
      } catch (err) {
        throw new Error(err?.response?.data?.message || err?.message || "Serial details load failed");
      }
    },
    [branchId, requestConfig]
  );

  /* ------------------------- INIT FLOW ------------------------- */
  useEffect(() => {
    let ignore = false;

    (async () => {
      try {
        setLoading(true);

        await loadDropdowns();
        if (ignore) return;

        if (isEdit) {
          await loadForEdit(batteryIdFromUrl);
        } else {
          setFormData((prev) => ({
            ...prev,
            batteryId: 0,
          }));
          await loadNewCode();
        }
      } catch (err) {
        toast.error(err?.message || "Failed to load");
      } finally {
        if (!ignore) setLoading(false);
      }
    })();

    return () => {
      ignore = true;
    };
  }, [batteryIdFromUrl, isEdit, loadDropdowns, loadForEdit, loadNewCode]);

  /* ------------------------- HANDLERS ------------------------- */
  const handleInputChange = async (e) => {
    const { name, value } = e.target;

    if (isView) return;

    setField(name, value);

    if (name === "vehicleId") {
      const vid = value;
      setField("vehicleRegNoId", vid);

      if (vid && vid !== "Select") {
        try {
          await loadVehicleRegNos(vid);
        } catch (err) {
          toast.error(err.message);
        }
      } else {
        setVehicleRegNos([]);
      }
    }

    // If vehicle reg no changed manually, sync vehicleId too
    if (name === "vehicleRegNoId") {
      setField("vehicleId", value);
    }
  };

  const handleSerialBlur = async () => {
    if (isView) return;

    try {
      await loadSerialDetails(formData.batterySerialNo);
    } catch (err) {
      toast.error(err.message);
    }
  };

  const validate = () => {
    if (!formData.dateOfReceipt) return "Date of Receipt is required";
    if (!formData.vehicleId) return "Vehicle Code is required";
    if (!formData.batterySerialNo) return "Battery Serial No is required";
    if (!formData.batteryType) return "Battery Type is required";
    if (!formData.batteryMake) return "Battery Make is required";
    if (!formData.storeId) return "Store is required";
    if (!formData.expectedLifeYear || formData.expectedLifeYear === "Select") return "Expected Life In Years is required";
    if (!formData.expectedLifeMonth || formData.expectedLifeMonth === "Select") return "Expect Life In Months is required";
    if (!formData.warrantyFrom) return "Warranty Period From is required";
    if (!formData.warrantyTo) return "Warranty Period To is required";
    return "";
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (isView) return;

    const msg = validate();
    if (msg) {
      toast.error(msg);
      return;
    }

    try {
      setLoading(true);

      const payload = {
        batteryId: isEdit ? batteryIdFromUrl : 0,
        batteryCode: formData.batteryCode,

        branchId,
        storeId: toInt(formData.storeId),
        vehicleId: toInt(formData.vehicleId),

        batterySerialNo: formData.batterySerialNo,
        batteryType: formData.batteryType,
        batteryMake: formData.batteryMake,

        expectedLifeYear: formData.expectedLifeYear,
        expectedLifeMonth: formData.expectedLifeMonth,

        purchasedFromId: toInt(formData.purchasedFromId),
        purchaseAmt: formData.purchaseAmt,
        purchasedDate: formData.purchasedDate,

        dateOfReceipt: formData.dateOfReceipt,
        warrantyFrom: formData.warrantyFrom,
        warrantyTo: formData.warrantyTo,

        remarks: formData.remarks,
        srcSerialNo: formData.srcSerialNo,

        loginUserId,
      };

      const { data } = await axios.post(
        `${API_BASE}/save`,
        payload,
        requestConfig
      );

      if (!data?.success) {
        throw new Error(data?.message || "Save failed");
      }

      toast.success(data.message || "Saved");

      const savedId = toInt(data.batteryId);

      if (!isEdit && savedId > 0) {
        navigate(`/vehicle/BatteryCardEntry?batteryId=${savedId}`, { replace: true });
      }
    } catch (err) {
      toast.error(err?.response?.data?.message || err?.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleResetNew = async () => {
    try {
      setLoading(true);

      setVehicleRegNos([]);
      setFormData({
        batteryId: 0,
        batteryCode: "",
        dateOfReceipt: "",
        vehicleId: "",
        vehicleRegNoId: "",
        batterySerialNo: "",
        batteryType: "",
        batteryMake: "",
        expectedLifeYear: "Select",
        expectedLifeMonth: "Select",
        purchasedFromId: "0",
        purchaseAmt: "",
        purchasedDate: "",
        warrantyFrom: "",
        warrantyTo: "",
        remarks: "",
        storeId: "",
        srcSerialNo: "",
      });

      await loadNewCode();
      navigate(`/vehicle/BatteryCardEntry`, { replace: true });
    } catch (err) {
      toast.error(err?.message || "Failed to reset");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => navigate(-1);

  return (
    <VehicleLayoutWrapper>
      <div className={`min-h-screen p-6 ${isDark ? "bg-gray-900" : "bg-gray-100"}`}>
        <HeaderBar
          title={<span className="font-dmserif text-3xl">BATTERY CARD</span>}
          setSkin={setSkin}
          currentSkin={skin}
        />

        <div
          className={`p-6 mt-6 rounded-2xl shadow-xl border ${
            isDark
              ? "bg-gradient-to-br from-white/20 to-white/5 backdrop-blur-md border-white/10 text-white"
              : `bg-white ${currentSkin.glass} text-black`
          }`}
        >
          <form onSubmit={handleSubmit}>
            {/* Header */}
            <div className="flex justify-between items-center mb-8">
              <div className="flex items-center gap-4">
                <h1 className={`text-2xl font-bold ${isDark ? "text-white" : "text-gray-900"}`}>
                  {isView
                    ? `Battery Card View (ID: ${batteryIdFromUrl})`
                    : isEdit
                    ? `Battery Card Edit (ID: ${batteryIdFromUrl})`
                    : "Battery Card Entry"}
                </h1>

                {loading && <span className="text-sm opacity-70">Loading...</span>}
              </div>

              <div className="flex items-center gap-3">
                {!isEdit && !isView && (
                  <button
                    type="button"
                    onClick={handleResetNew}
                    className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                      isDark
                        ? "border-gray-600 text-white hover:bg-gray-800"
                        : "border-gray-300 text-gray-700 hover:bg-gray-50"
                    }`}
                  >
                    <FaPlus className="w-4 h-4" />
                    New
                  </button>
                )}

                <button
                  type="button"
                  onClick={handleCancel}
                  className={`flex items-center gap-2 px-4 py-2 border rounded-lg transition-colors ${
                    isDark
                      ? "border-gray-600 text-white hover:bg-gray-800"
                      : "border-gray-300 text-gray-700 hover:bg-gray-50"
                  }`}
                >
                  <FaTimes className="w-4 h-4" />
                  Cancel
                </button>

                {!isView && (
                  <button
                    type="submit"
                    disabled={loading}
                    className={`flex items-center gap-2 ${buttonClass} ${hoverClass} text-white px-4 py-2 rounded transition-colors disabled:opacity-60`}
                  >
                    <FaSave className="w-4 h-4" />
                    {isEdit ? "Update" : "Save"}
                  </button>
                )}
              </div>
            </div>

            {/* Main grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-6">
              {/* Column 1 */}
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Battery Code</label>
                  <input
                    type="text"
                    value={formData.batteryCode}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    readOnly
                  />
                </div>

                <div>
                  <label className={labelClass}>Date of Receipt *</label>
                  <input
                    type="date"
                    name="dateOfReceipt"
                    value={formData.dateOfReceipt}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  />
                </div>

                <div>
                  <label className={labelClass}>Vehicle Reg No</label>
                  <select
                    name="vehicleRegNoId"
                    value={formData.vehicleRegNoId}
                    onChange={handleInputChange}
                    className={`${selectClass} ${readOnlyFieldClass}`}
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {vehicleRegNos.map((x) => (
                      <option key={`${x.id}-${x.regNo}`} value={String(x.id)}>
                        {x.regNo}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Make *</label>
                  <input
                    type="text"
                    name="batteryMake"
                    value={formData.batteryMake}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  />
                </div>
              </div>

              {/* Column 2 */}
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Expect Life In Months *</label>
                  <select
                    name="expectedLifeMonth"
                    value={formData.expectedLifeMonth}
                    onChange={handleInputChange}
                    className={`${selectClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  >
                    {expectedLifeMonths.map((m) => (
                      <option key={m} value={m}>
                        {m === "Select" ? "Select" : m}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Purchased Amount</label>
                  <input
                    type="number"
                    name="purchaseAmt"
                    value={formData.purchaseAmt}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    disabled={isView}
                  />
                </div>

                <div>
                  <label className={labelClass}>Warranty Period From *</label>
                  <input
                    type="date"
                    name="warrantyFrom"
                    value={formData.warrantyFrom}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  />
                </div>

                <div>
                  <label className={labelClass}>Remarks</label>
                  <textarea
                    name="remarks"
                    value={formData.remarks}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    rows="2"
                    disabled={isView}
                  />
                </div>
              </div>

              {/* Column 3 */}
              <div className="space-y-6">
                <div>
                  <label className={labelClass}>Store *</label>
                  <select
                    name="storeId"
                    value={formData.storeId}
                    onChange={handleInputChange}
                    className={`${selectClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {stores.map((s) => (
                      <option key={s.id} value={String(s.id)}>
                        {s.name}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Vehicle Code *</label>
                  <select
                    name="vehicleId"
                    value={formData.vehicleId}
                    onChange={handleInputChange}
                    className={`${selectClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  >
                    <option value="">Select</option>
                    {vehicleCodes.map((v) => (
                      <option key={v.id} value={String(v.id)}>
                        {v.code}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className={labelClass}>Battery Serial No *</label>
                  <input
                    type="text"
                    name="batterySerialNo"
                    value={formData.batterySerialNo}
                    onChange={handleInputChange}
                    onBlur={handleSerialBlur}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  />
                </div>

                <div>
                  <label className={labelClass}>Battery Type *</label>
                  <input
                    type="text"
                    name="batteryType"
                    value={formData.batteryType}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    required
                    disabled={isView}
                  />
                </div>
              </div>
            </div>

            {/* Second row */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
              <div>
                <label className={labelClass}>Expected Life In Years *</label>
                <select
                  name="expectedLifeYear"
                  value={formData.expectedLifeYear}
                  onChange={handleInputChange}
                  className={`${selectClass} ${readOnlyFieldClass}`}
                  required
                  disabled={isView}
                >
                  {expectedLifeYears.map((y) => (
                    <option key={y} value={y}>
                      {y === "Select" ? "Select" : y}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Purchase From</label>
                <select
                  name="purchasedFromId"
                  value={formData.purchasedFromId}
                  onChange={handleInputChange}
                  className={`${selectClass} ${readOnlyFieldClass}`}
                  disabled={isView}
                >
                  <option value="0">Select</option>
                  {suppliers.map((s) => (
                    <option key={s.id} value={String(s.id)}>
                      {s.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className={labelClass}>Issued / Purchased Date</label>
                <input
                  type="date"
                  name="purchasedDate"
                  value={formData.purchasedDate}
                  onChange={handleInputChange}
                  className={`${inputClass} ${readOnlyFieldClass}`}
                  disabled={isView}
                />
              </div>

              <div>
                <label className={labelClass}>Warranty Period To *</label>
                <input
                  type="date"
                  name="warrantyTo"
                  value={formData.warrantyTo}
                  onChange={handleInputChange}
                  className={`${inputClass} ${readOnlyFieldClass}`}
                  required
                  disabled={isView}
                />
              </div>
            </div>

            {/* SRC serial */}
            {showSrcSerialNo && (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                <div>
                  <label className={labelClass}>SRC Serial No</label>
                  <input
                    type="text"
                    name="srcSerialNo"
                    value={formData.srcSerialNo}
                    onChange={handleInputChange}
                    className={`${inputClass} ${readOnlyFieldClass}`}
                    disabled={isView}
                  />
                </div>
              </div>
            )}

            {/* Footer */}
            <div className={`mt-8 pt-6 border-t ${isDark ? "border-gray-700" : "border-gray-200"}`}>
              <p className={`text-sm text-center ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                INFRA SCREEN : B-6/1
              </p>
            </div>
          </form>

          <ToastContainer
            position="top-right"
            autoClose={3000}
            closeOnClick
            pauseOnHover
            theme={isDark ? "dark" : "light"}
          />
        </div>
      </div>
    </VehicleLayoutWrapper>
  );
};

export default BatteryCardEntry;